import {
  addDays,
  endOfDay,
  format,
  fromUnixTime,
  startOfDay,
  startOfWeek,
  subDays,
} from 'date-fns';

export const formatDate = (timestamp: number, formatStr: string = 'HH:mm, dd.MM.yyyy') =>
  format(fromUnixTime(timestamp), formatStr);

export const getEndOfDay = (dateTime: Date = new Date()) => endOfDay(dateTime);

export const getStartOfDay = (dateTime: Date = new Date()) => startOfDay(dateTime);

export const getStartOfWeek = (dateTime: Date = new Date()) =>
  startOfWeek(dateTime, { weekStartsOn: 1 }); // Monday as start of week

export const getPreviousDayStart = (dateTime: Date = new Date()) =>
  startOfDay(subDays(dateTime, 1));

export const getNextDayStart = (dateTime: Date = new Date()) => {
  const currentMoment = new Date();
  const baseDate = dateTime >= currentMoment ? dateTime : currentMoment;
  return startOfDay(addDays(baseDate, 1));
};

export const getCurrentDayStart = (): Date => startOfDay(new Date());

export const toTimestamp = (date: number | Date): number =>
  Math.floor(new Date(date).getTime() / 1000);

export const secondsFromMidnightToHHMM = (secondsFromMidnight: number | null): string => {
  if (secondsFromMidnight === null) {
    return '';
  }
  const h = `0${Math.floor(secondsFromMidnight / 3600)}`.slice(-2);
  const m = `0${Math.floor(Math.floor((secondsFromMidnight % 3600) / 60))}`.slice(-2);
  return `${h}:${m}`;
};

export const HHMMSStoSeconds = (
  timeString: string,
  separator = ':',
  forceSeconds?: number,
): number | null => {
  if (timeString.length < 5) {
    return null;
  }

  const timeParts = timeString.split(separator);
  if (timeParts.length === 1) {
    return null;
  }
  const hoursNumber = parseInt(timeParts[0], 10);
  if (Number.isNaN(hoursNumber) || hoursNumber > 23) {
    return null;
  }

  const minutesNumber = parseInt(timeParts[1], 10);
  if (Number.isNaN(minutesNumber) || minutesNumber > 59) {
    return null;
  }

  let secondsNumber = timeParts[2] ? parseInt(timeParts[2], 10) : 0;
  if (Number.isNaN(secondsNumber) || secondsNumber > 59) {
    return null;
  }

  if (forceSeconds) {
    secondsNumber = forceSeconds;
  }

  return hoursNumber * 3600 + minutesNumber * 60 + secondsNumber;
};

/**
 * Converts an IANA timezone name (e.g., "Europe/Bucharest") to GMT offset format (e.g., "GMT+3").
 * @param ianaTimezone - IANA timezone identifier like "Europe/Bucharest", "America/New_York"
 * @returns GMT offset string like "GMT+3", "GMT-5", or "GMT+5:30" for non-whole-hour offsets
 */
export const formatTimezoneToGMTOffset = (ianaTimezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

    if (timeZonePart?.value) {
      // The value will be like "GMT+3" or "GMT-5" or "GMT+5:30"
      return timeZonePart.value;
    }

    return ianaTimezone;
  } catch {
    // If the timezone is invalid, return it as-is
    return ianaTimezone;
  }
};
